import { useEffect, useRef } from 'react'
import { Badge, Button, Drawer, useDrawer } from '@iveco-ti/iveco-ds'
import { useIntersectionObserver } from '@uidotdev/usehooks'
import { Pin } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePinnedNews } from '../hooks/usePinnedNews'
import { NewsCard } from './NewsCard'
import { NewsCardSkeleton } from './NewsCardSkeleton'

export function DrawerPinnedNews() {
  const { t } = useTranslation('panel')
  const {
    pinnedNews,
    isLoadingPinnedNews,
    totalPinnedNews,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePinnedNews()
  const drawerPinnedNews = useDrawer('drawer-pinned-news')

  const endElementRef = useRef<HTMLDivElement>(null)

  const [endElement, entry] = useIntersectionObserver({
    threshold: 0,
    root: null,
    rootMargin: '0px',
  })

  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [entry?.isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    if (endElementRef.current) {
      endElement(endElementRef.current)
    }
  }, [endElement])

  return (
    <Drawer.Root id="drawer-pinned-news">
      <Drawer.Header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg text-brand-800 font-semibold">
            {t('news.drawer_pinned_news.header.title')}
          </h1>

          {totalPinnedNews > 0 && (
            <Badge.Root size="sm">{totalPinnedNews}</Badge.Root>
          )}
        </div>
      </Drawer.Header>

      <Drawer.Body>
        <div className="space-y-4">
          {isLoadingPinnedNews && (
            <div className="space-y-4">
              <NewsCardSkeleton />
              <NewsCardSkeleton />
              <NewsCardSkeleton />
            </div>
          )}

          {!isLoadingPinnedNews && pinnedNews.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Pin className="w-12 h-12 text-neutral-400 mb-4" />

              <h3 className="text-lg font-medium text-neutral-600 mb-2">
                {t('news.drawer_pinned_news.empty_state.title')}
              </h3>

              <p className="text-neutral-500 text-sm max-w-sm">
                {t('news.drawer_pinned_news.empty_state.description')}
              </p>
            </div>
          )}

          {!isLoadingPinnedNews &&
            pinnedNews.map((news) => <NewsCard key={news.id} post={news} />)}

          <div ref={endElementRef} />

          {isFetchingNextPage && <NewsCardSkeleton />}
        </div>
      </Drawer.Body>

      <Drawer.Footer>
        <Button.Root
          variant="secondary"
          color="brand"
          onClick={drawerPinnedNews.close}
        >
          {t('news.drawer_pinned_news.footer.button_close')}
        </Button.Root>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
